/**
 * Themen (spec 10.2): every published topic. This is the teacher's preview of what their class
 * sees — there is no "view as student" mode, so the list leads to the topic page rather than
 * into the feed.
 */

import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { authedJson } from "../shared/api.js";
import type { TeacherTopic } from "../shared/api-types.js";
import { germanMessage } from "../shared/errors.js";
import { Empty, ErrorLine, Loading, Panel, formatDate } from "./ui.js";

export function TopicsScreen() {
  const topics = useQuery({
    queryKey: ["teacher", "topics"],
    queryFn: () => authedJson<TeacherTopic[]>("/teacher/topics", { subjectType: "teacher" }),
  });

  if (topics.isPending) return <Loading />;
  if (topics.isError) return <ErrorLine>{germanMessage(topics.error)}</ErrorLine>;

  return (
    <>
      <h1 className="mt-0 mb-5 text-[26px] font-bold">Themen</h1>
      <Panel
        title={
          (topics.data?.length ?? 0) === 1
            ? "1 veröffentlichtes Thema"
            : (topics.data?.length ?? 0) + " veröffentlichte Themen"
        }
      >
        {topics.data && topics.data.length === 0 ? (
          <Empty>Noch keine veröffentlichten Themen.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="t-table">
              <thead>
                <tr>
                  <th scope="col">Titel</th>
                  <th scope="col">Epoche</th>
                  <th scope="col">Karten</th>
                  <th scope="col">Veröffentlicht</th>
                </tr>
              </thead>
              <tbody>
                {topics.data?.map((topic) => (
                  <tr key={topic.id}>
                    <td>
                      <Link
                        to={"/lehrer/themen/" + topic.slug}
                        className="font-semibold text-[var(--color-accent)] no-underline hover:underline"
                      >
                        {topic.title}
                      </Link>
                    </td>
                    <td>{topic.era}</td>
                    <td className="t-num">{topic.cardCount}</td>
                    <td>{formatDate(topic.publishedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
